// @ts-nocheck
//
// Single place where all the `window.*` handlers referenced from index.html
// (and from inline onclick attributes inside modal templates) are registered.
//
// Importing this file once for side-effects from main.ts is enough; no other
// module should reach for `window.*` to set handlers.

import {
  openAuthModal,
  closeAuthModal,
  switchAuthMode,
  submitAuthForm,
  openForgotPassword,
  backToLogin,
  backToRegistration,
  resendRegistrationCode,
  getCurrentUser,
} from "./features/auth-flow";
import {
  closeAccountMenuModal,
  logoutCurrentUser,
  onAccountButton,
  openCommunityMemories,
} from "./features/account-menu";
import {
  openPurchaseModal,
  closeModal,
  proceedToPayment,
  createGrave,
  openCreateMemorial,
} from "./features/purchase";
import {
  startPethernityCheckout,
  openPaywall,
} from "./features/pethernity";
import { closePaywallModal } from "./components/modals/paywall";
import {
  openMyHeadstones,
  closeMyHeadstonesModal,
} from "./features/my-headstones";
import {
  openMemorialCatalog,
  closeMemorialCatalogModal,
} from "./features/memorial-catalog";
import { checkPaymentAndCreateGrave } from "./features/payment-return";
import {
  shareMemorialFromView,
  shareMemorialToFacebook,
  shareMemorialToInstagram,
  shareMemorialToWhatsApp,
  shareMemorialToX,
  shareMemorialToEmail,
  shareMemorialCopyLink,
} from "./features/share-memorial";
import {
  goToThanksProfile,
  goToThanksGarden,
  shareThanksWhatsApp,
  shareThanksFacebook,
  shareThanksInstagram,
  shareThanksX,
  shareThanksEmail,
  shareThanksCopyLink,
} from "./features/payment-thanks";
import { goHome, toggleZoom } from "./map/camera";
import { findEmptySpace } from "./map/find-empty";
import { goToMyMemorial } from "./features/go-to-my-memorial";
import { closeViewModal } from "./map/grave-render";
import {
  openEditHeadstoneModal,
  closeEditHeadstoneModal,
  submitEditHeadstone,
} from "./features/edit-headstone";
import {
  lightCandleFromView,
  deleteGuestMessageFromView,
  openTributesLogin,
  giftFlowerFromView,
} from "./features/memorial-tributes";
import { reopenCookieConsent } from "./components/consent-banner/consent-banner";
import { showView } from "./features/view-router";

declare global {
  interface Window {
    goHome: typeof goHome;
    toggleZoom: typeof toggleZoom;
    findEmptySpace: typeof findEmptySpace;
    goToMyMemorial: typeof goToMyMemorial;
    onAccountButton: typeof onAccountButton;
    openAuthModal: typeof openAuthModal;
    closeAuthModal: typeof closeAuthModal;
    switchAuthMode: typeof switchAuthMode;
    submitAuthForm: typeof submitAuthForm;
    openForgotPassword: typeof openForgotPassword;
    backToLogin: typeof backToLogin;
    backToRegistration: typeof backToRegistration;
    resendRegistrationCode: typeof resendRegistrationCode;
    getCurrentUser: typeof getCurrentUser;
    closeAccountMenuModal: typeof closeAccountMenuModal;
    openCommunityMemories: typeof openCommunityMemories;
    logoutCurrentUser: typeof logoutCurrentUser;
    proceedToPayment: typeof proceedToPayment;
    createGrave: typeof createGrave;
    openCreateMemorial: typeof openCreateMemorial;
    startPethernityCheckout: typeof startPethernityCheckout;
    openPethernityPaywall: typeof openPaywall;
    closePethernityPaywall: typeof closePaywallModal;
    closeModal: typeof closeModal;
    closeViewModal: typeof closeViewModal;
    openEditHeadstoneModal: typeof openEditHeadstoneModal;
    closeEditHeadstoneModal: typeof closeEditHeadstoneModal;
    submitEditHeadstone: typeof submitEditHeadstone;
    openMyHeadstones: typeof openMyHeadstones;
    closeMyHeadstonesModal: typeof closeMyHeadstonesModal;
    openMemorialCatalog: typeof openMemorialCatalog;
    closeMemorialCatalogModal: typeof closeMemorialCatalogModal;
    checkPaymentAndCreateGrave: typeof checkPaymentAndCreateGrave;
    shareMemorialFromView: typeof shareMemorialFromView;
    shareMemorialToFacebook: typeof shareMemorialToFacebook;
    shareMemorialToInstagram: typeof shareMemorialToInstagram;
    shareMemorialToWhatsApp: typeof shareMemorialToWhatsApp;
    shareMemorialToX: typeof shareMemorialToX;
    shareMemorialToEmail: typeof shareMemorialToEmail;
    shareMemorialCopyLink: typeof shareMemorialCopyLink;
    goToThanksProfile: typeof goToThanksProfile;
    goToThanksGarden: typeof goToThanksGarden;
    shareThanksWhatsApp: typeof shareThanksWhatsApp;
    shareThanksFacebook: typeof shareThanksFacebook;
    shareThanksInstagram: typeof shareThanksInstagram;
    shareThanksX: typeof shareThanksX;
    shareThanksEmail: typeof shareThanksEmail;
    shareThanksCopyLink: typeof shareThanksCopyLink;
    lightCandleFromView: typeof lightCandleFromView;
    giftFlowerFromView: typeof giftFlowerFromView;
    deleteGuestMessageFromView: typeof deleteGuestMessageFromView;
    openTributesLogin: typeof openTributesLogin;
    reopenCookieConsent: typeof reopenCookieConsent;
  }
}

// Camera / zoom
// goHome / goToMyMemorial double as "back to the garden" from any full-screen
// view, so they return to the garden view before running the camera logic.
window.goHome = () => {
  showView("garden");
  goHome();
};
window.toggleZoom = toggleZoom;
window.findEmptySpace = findEmptySpace;
window.goToMyMemorial = () => {
  showView("garden");
  goToMyMemorial();
};

// Account / auth
window.onAccountButton = onAccountButton;
window.openAuthModal = openAuthModal;
window.closeAuthModal = closeAuthModal;
window.switchAuthMode = switchAuthMode;
window.submitAuthForm = submitAuthForm;
window.openForgotPassword = openForgotPassword;
window.backToLogin = backToLogin;
window.backToRegistration = backToRegistration;
window.resendRegistrationCode = resendRegistrationCode;
window.getCurrentUser = getCurrentUser;
window.closeAccountMenuModal = closeAccountMenuModal;
window.openCommunityMemories = openCommunityMemories;
window.logoutCurrentUser = logoutCurrentUser;

// Purchase / view modals
window.proceedToPayment = proceedToPayment;
window.createGrave = createGrave;
window.openCreateMemorial = openCreateMemorial;
window.startPethernityCheckout = () => {
  closePaywallModal();
  startPethernityCheckout().catch(() => null);
};
window.openPethernityPaywall = () => openPaywall();
window.closePethernityPaywall = closePaywallModal;
window.closeModal = closeModal;
window.closeViewModal = closeViewModal;
window.openEditHeadstoneModal = openEditHeadstoneModal;
window.closeEditHeadstoneModal = closeEditHeadstoneModal;
window.submitEditHeadstone = submitEditHeadstone;

// My headstones
window.openMyHeadstones = openMyHeadstones;
window.closeMyHeadstonesModal = closeMyHeadstonesModal;
window.openMemorialCatalog = openMemorialCatalog;
window.closeMemorialCatalogModal = closeMemorialCatalogModal;

// Stripe return
window.checkPaymentAndCreateGrave = checkPaymentAndCreateGrave;

// Memorial share
window.shareMemorialFromView = shareMemorialFromView;
window.shareMemorialToFacebook = () => shareMemorialToFacebook();
window.shareMemorialToInstagram = () => {
  shareMemorialToInstagram().catch(() => null);
};
window.shareMemorialCopyLink = () => {
  shareMemorialCopyLink().catch(() => null);
};
window.shareMemorialToWhatsApp = () => shareMemorialToWhatsApp();
window.shareMemorialToX = () => shareMemorialToX();
window.shareMemorialToEmail = () => shareMemorialToEmail();

window.goToThanksProfile = goToThanksProfile;
window.goToThanksGarden = goToThanksGarden;
window.shareThanksWhatsApp = shareThanksWhatsApp;
window.shareThanksFacebook = shareThanksFacebook;
window.shareThanksInstagram = shareThanksInstagram;
window.shareThanksX = shareThanksX;
window.shareThanksEmail = shareThanksEmail;
window.shareThanksCopyLink = () => {
  shareThanksCopyLink();
};

window.lightCandleFromView = () => {
  lightCandleFromView().catch(() => null);
};
window.giftFlowerFromView = () => {
  giftFlowerFromView().catch(() => null);
};
window.deleteGuestMessageFromView = (messageId: string) => {
  deleteGuestMessageFromView(messageId).catch(() => null);
};
window.openTributesLogin = openTributesLogin;
window.reopenCookieConsent = reopenCookieConsent;
